import { useQuery } from "@tanstack/react-query";
import { api } from "../api";

/**
 * Fetches a Stripe SetupIntent client secret for the given user.
 *
 * Uses useQuery (cached by userId in the queryClient) rather than useMutation
 * so the result survives React.StrictMode's mount → unmount → remount cycle
 * in dev — useMutation observers are torn down on unmount, orphaning the
 * in-flight response. useQuery caches the data globally.
 *
 * The underlying endpoint is POST, but from the client's perspective this is
 * a "fetch the client secret for this user" operation.
 *
 * staleTime is set to 24 hours to align with Stripe's SetupIntent validity
 * window. This prevents background refetches from replacing the clientSecret
 * while the user is filling in the payment form, which would remount Stripe
 * Elements and discard any partial card input. retry is disabled so a
 * transient error surfaces immediately rather than creating multiple SIs.
 */
export function useSetupIntent(userId: string | undefined) {
  return useQuery({
    queryKey: ["setup-intent", userId],
    queryFn: () => api.setupIntent(userId!).then((r) => r.data),
    enabled: !!userId,
    staleTime: 24 * 60 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
}
